export const toSnakeCase = (s: string): string =>
  s
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

const NAME_RE = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export const isValidEventName = (name: string): boolean =>
  name.startsWith('$') || NAME_RE.test(name);

export const isValidPropertyKey = (key: string): boolean => NAME_RE.test(key);

export const ALLOWED_VERB_SUFFIXES: readonly string[] = [
  'created', 'viewed', 'clicked', 'selected', 'started', 'finished', 'submitted', 'opened',
  'closed', 'shared', 'updated', 'deleted', 'failed', 'quit', 'resolved', 'interacted',
  'recovered', 'signed_up', 'signed_in', 'depleted',
  'profiled',
  'requested',
  'saved', 'claimed', 'purchased', 'granted',
];

export const isAllowedEventVerb = (name: string): boolean =>
  name.startsWith('$') ||
  ALLOWED_VERB_SUFFIXES.some((v) => name === v || name.endsWith(`_${v}`));

export const KNOWN_PROPERTIES: ReadonlySet<string> = new Set<string>([
  'activity_type',
  'age_seconds',
  'ambassador_partner',
  'already_knew_count',
  'angle',
  'answer',
  'api_route',
  'app_languages',
  'app_version',
  'attempt_duration_ms',
  'auth_method',
  'available_count',
  'badge_code',
  'badge_codes',
  'battery_level',
  'badge_family',
  'badge_tier',
  'block_type',
  'boot_path',
  'button_name',
  'button_text_content',
  'bytes_missing',
  'bytes_total',
  'can_challenge_parent',
  'capture_method',
  'card_index',
  'card_rarity',
  'card_type',
  'cards_count',
  'celebration_name',
  'celebration_trigger',
  'change_source',
  'chapter_id',
  'chapter_kind',
  'chapter_uuid',
  'child_id',
  'chip_kind',
  'children_count',
  'claim_count',
  'claim_source',
  'code',
  'completed_steps_count',
  'connection_type',
  'consent_status',
  'control_feat_resolved',
  'control_feat_variant',
  'conversation_id',
  'conversation_kind',
  'conversation_position',
  'countries',
  'cta',
  'celebration_variant',
  'cta_action',
  'cta_position',
  'currency',
  'current_smartbit_index',
  'current_streak',
  'current_step_index',
  'current_step_kind',
  'current_step_scope',
  'daily_count',
  'days_covered',
  'destination',
  'detail_context',
  'device_model',
  'device_os_version',
  'device_platform',
  'direction',
  'dismiss_method',
  'display_mode',
  'download_attempts',
  'duration',
  'duration_ms',
  'error',
  'error_action',
  'error_kind',
  'error_message_id',
  'error_screen',
  'error_type',
  'entry_point',
  'evaluation_date',
  'exam_id',
  'exam_piece_id',
  'exercise_index',
  'failed_criteria',
  'failure_detail',
  'failure_reason',
  'fallback_reason',
  'family_code',
  'family_created_date',
  'family_id',
  'family_subscription_plan',
  'family_subscription_status',
  'feature_type',
  'file_size_bytes',
  'files_missing',
  'files_missing_after',
  'files_total',
  'filled_slots',
  'floor',
  'flow_source',
  'focus_source',
  'freezes_held',
  'freezes_left',
  'frequency',
  'friend_request_answer',
  'friend_request_source',
  'friends_count',
  'from_lesson_id',
  'from_tier',
  'from_user',
  'from_version',
  'funnel_country',
  'goal_days',
  'gesture',
  'has_badge',
  'has_crop',
  'has_data',
  'has_entitlement',
  'has_lesson_chapters',
  'has_lesson_digest',
  'heap_used_mb',
  'hearts_remaining',
  'height',
  'held_after',
  'hint_number',
  'hook',
  'history_bubble_count',
  'history_section',
  'help_source',
  'help_step',
  'history_source',
  'http_status',
  'hub_line',
  'identity_outcome',
  'identity_trigger',
  'imported_for_child_id',
  'input_method',
  'intro_price_package_types',
  'invitations_count',
  'is_block_lesson',
  'is_block_session',
  'is_completed',
  'is_enabled',
  'is_owned',
  'owned_count',
  'practice_part',
  'stream_width',
  'stream_height',
  'caps_max_width',
  'caps_max_height',
  'focus_modes',
  'stream_request',
  'shutter_ms',
  'converge_ms',
  'encode_ms',
  'encode_draw_ms',
  'encode_blob_ms',
  'score_ms',
  'still_ms',
  'frames_presented',
  'frames_scored',
  'best_card_rarity',
  'best_frame_index',
  'first_score',
  'best_score',
  'mean_luma',
  'focus_tap_count',
  'still_attempted',
  'still_policy',
  'still_width',
  'still_height',
  'still_score',
  'still_similarity',
  'still_rejected_reason',
  'view_width',
  'view_height',
  'preview_fill_pct',
  'frame_kept_pct',
  'is_fallback',
  'camera_profile',
  'track_label',
  'back_camera_count',
  'lens_source',
  'lens_attempts',
  'lens_switch_ms',
  'overconstrained_constraint',
  'encode_path',
  'draw_count',
  'draw_ms_max',
  'capture_mode',
  'phase',
  'session_age_ms',
  'last_progress_percent',
  'last_visibility',
  'hidden_for_ms',
  'scorer',
  'scorer_fallback',
  'hardware_concurrency',
  'is_first',
  'is_goal_reached',
  'is_grouping_pending',
  'is_last_item',
  'is_meta',
  'is_notify_requested',
  'is_monthly_offer_shown',
  'is_paid',
  'is_purchase_completed',
  'is_rc_customer_created',
  'is_record',
  'is_resumed',
  'is_restart',
  'is_retake',
  'is_retry',
  'is_chapter_expanded',
  'is_charging',
  'is_self',
  'is_sheet_open',
  'is_skipped',
  'is_success',
  'is_unlocked',
  'js_boot_ms',
  'late_ms',
  'item_index',
  'lesson_id',
  'lesson_kind',
  'lesson_piece_id',
  'lesson_suggestion_count',
  'lesson_uuid',
  'mate_status',
  'message',
  'message_count',
  'message_length',
  'month',
  'method_name',
  'ms_since_lesson_created',
  'offering_id',
  'outcome',
  'onesignal_wait_outcome',
  'onesignal_wait_ms',
  'package_count',
  'package_type',
  'package_types',
  'page_path',
  'page_route',
  'parents_count',
  'path',
  'paywall_result',
  'paywall_type',
  'permission',
  'permission_status',
  'photo_check_severity',
  'photo_check_trigger',
  'photo_context',
  'photo_source',
  'photo_status',
  'photos_count',
  'placeholder_example_key',
  'placement',
  'platform',
  'position',
  'practice_kind',
  'practice_screen',
  'price',
  'probe_ms',
  'product_id',
  'product_ids',
  'profile_open_source',
  'profile_user_uuid',
  'purchase_source',
  'quantity',
  'question_id',
  'question_type',
  'questions_answered_count',
  'questions_correct_count',
  'questions_count',
  'quiz_cognition',
  'quiz_id',
  'quiz_run_id',
  'quiz_type',
  'quizzes_count',
  'rating',
  'ready_ms',
  'ready_resource_kinds',
  'record_streak',
  'redirect_path',
  'referees_count',
  'referral_source',
  'referrer_type',
  'release_reason',
  'reload_cover_age_ms',
  'reload_cover_branch',
  'reload_cover_show',
  'reload_cover_source',
  'resource_kind',
  'resource_order',
  'response',
  'revenue',
  'revision_sheet_position',
  'revision_sheets_count',
  'reward_amount',
  'reward_babz',
  'reward_type',
  'road_source',
  'road_tier',
  'rotation',
  'route',
  'run_number',
  'score',
  'season_type',
  'section_number',
  'session_id',
  'share_method',
  'share_source',
  'showcase_source',
  'showcased_badges_count',
  'showcased_count',
  'shown_count',
  'skip_reason',
  'skipped_count',
  'slots_count',
  'smartbit_position',
  'smartbits_count',
  'stage',
  'star_amount',
  'star_road_tier',
  'star_source',
  'stars_total',
  'start_source',
  'streak_source',
  'step_id',
  'step_index',
  'step_kind',
  'step_part_index',
  'step_parts_count',
  'step_scope',
  'step_status',
  'steps_count',
  'streak_state',
  'subject',
  'subjects',
  'subjects_count',
  'submit_method',
  'subscription_plan',
  'subscription_status',
  'suggestion_count',
  'suggestion_kind',
  'suggestion_label',
  'support_source',
  'swipe_method',
  'swiped_count',
  'target',
  'template_code',
  'three_month_goals',
  'timetable_child_count',
  'timetable_file_type',
  'timetable_photo_count',
  'timetable_photo_source',
  'timetable_source',
  'to_lesson_id',
  'to_review_count',
  'to_tier',
  'to_user',
  'to_version',
  'toggle_state',
  'tone',
  'total_count',
  'transcript_length',
  'trigger',
  'unseen_count',
  'update_mode',
  'update_outcome',
  'user_role',
  'user_type',
  'validation_level',
  'viewed_tier',
  'viewer_role',
  'wallet_currency',
  'weekly_count',
  'width',
  'is_lesson_started',
  'progression_percent',
  'parent_lesson_tab',
  'is_from_empty_state',
  'knowledge_component_id',
  'kc_mastery',
  'is_chapter_status',
  'greeting_index',
  'is_current_profile',
  'profile_picker_source',
  'profiles_count',
  'selected_user_type',
]);

export const INTENTIONAL_DUPLICATES: ReadonlyArray<[string, string]> = [
  ['page_route', 'page_path'],
];

export const FORBIDDEN_BARE_PROPERTIES: ReadonlySet<string> = new Set<string>([
  'type', 'kind', 'status', 'source', 'method', 'category', 'state', 'mode', 'format',
  'role', 'action', 'reason',
]);
